import React from 'react';
import {matchPath, PathMatch, RouteObject} from 'react-router';
import HomeView from '../views/home/HomeView';
import UXView from '../views/ux/UXView';
import CustomerService from '../views/help/CustomerService';
import CareersView from '../views/careers/CareersView';
import Error500 from '../views/error/Error500';
import Error404 from '../views/error/Error404';
import ErrorJobNotFound from '../views/error/ErrorJobNotFound';
import CandidateView from '../views/candidate/CandidateView';
import EpicFaqView from '../views/faq/EpicFaqView';
import OptOutView from '../views/optout/OptOutView';
import {getPath} from '@epic-core/common';
import NewsView from '../views/news/NewsView';
import NewsDetailView from '../views/news/NewsDetailView';
import JobsView, {getJobsViewInitialSharedValues} from '../views/jobs/JobsView';
import JobsDetailView, {getJobsDetailViewInitialSharedValues} from '../views/jobs/JobsDetailView';
import GeneralLandingEGPage from '../views/GeneralLandingEGPage';
import {getAppRedirects} from './appRedirects';
import {useRoutes} from 'react-router-dom';
import {RouteMatchOrUrlParse, InitSharedValueResponse} from 'epic-ue-shared';
import URLParse from 'url-parse';
import CmsPage from '../CmsPage';

export interface ServerRoute {
    getInitialSharedValues?: (
        match: PathMatch | RouteMatchOrUrlParse,
        url: URLParse<{[key: string]: string | undefined}>
    ) => Promise<InitSharedValueResponse[]>;
    path?: string;
    element?: React.ReactNode | null;
}

/**
 * All app routes are defined here and have an additional property for determining
 * what data to load on the server and init into shared values
 */
export const getServerRoutes = (): ServerRoute[] => {
    const path = getPath();
    const redirects = getAppRedirects();

    return redirects.concat([
        {
            path: `${path}:locale/home`,
            getInitialSharedValues: HomeView.getInitialSharedValues,
            element: <HomeView />
        },
        {
            path: `${path}:locale/customer-service`,
            getInitialSharedValues: CustomerService.getInitialSharedValues,
            element: <CustomerService />
        },
        {
            path: `${path}:locale/ux`,
            getInitialSharedValues: UXView.getInitialSharedValues,
            element: <UXView />
        },
        {
            path: `${path}:locale/careers`,
            getInitialSharedValues: CareersView.getInitialSharedValues,
            element: <CareersView />
        },
        {
            path: `${path}:locale/careers/jobs`,
            getInitialSharedValues: getJobsViewInitialSharedValues,
            element: <JobsView />
        },
        {
            path: `${path}:locale/careers/jobs/:id`,
            getInitialSharedValues: getJobsDetailViewInitialSharedValues,
            element: <JobsDetailView />
        },
        {
            path: `${path}:locale/earlycareers`,
            getInitialSharedValues: GeneralLandingEGPage.getInitialSharedValues,
            element: <GeneralLandingEGPage />
        },
        {
            path: `${path}:locale/earlycareers/career-paths`,
            getInitialSharedValues: GeneralLandingEGPage.getInitialSharedValues,
            element: <GeneralLandingEGPage />
        },
        {
            path: `${path}:locale/candidateprivacynotice`,
            getInitialSharedValues: CandidateView.getInitialSharedValues,
            element: <CandidateView />
        },
        {
            path: `${path}:locale/free-fortnite-faq`,
            getInitialSharedValues: EpicFaqView.getInitialSharedValues,
            element: <EpicFaqView />
        },
        {
            path: `${path}:locale/freefortnite-australia`,
            getInitialSharedValues: EpicFaqView.getInitialSharedValues,
            element: <EpicFaqView />
        },
        {
            path: `${path}:locale/freefortnite-australia-press-release`,
            getInitialSharedValues: EpicFaqView.getInitialSharedValues,
            element: <EpicFaqView />
        },
        {
            path: `${path}:locale/freefortnite-uk-press-release`,
            getInitialSharedValues: EpicFaqView.getInitialSharedValues,
            element: <EpicFaqView />
        },
        {
            path: `${path}:locale/news`,
            getInitialSharedValues: NewsView.getInitialSharedValues,
            element: <NewsView />
        },
        {
            path: `${path}:locale/news/:title`,
            getInitialSharedValues: NewsDetailView.getInitialSharedValues,
            element: <NewsDetailView />
        },
        {
            path: `${path}:locale/news/:title`,
            getInitialSharedValues: NewsDetailView.getInitialSharedValues,
            element: <NewsDetailView />
        },
        {
            path: `${path}:locale/opt-out-confirmation`,
            getInitialSharedValues: OptOutView.getInitialSharedValues,
            element: <OptOutView />
        },
        {
            path: `${path}:locale/page-not-found`,
            element: <Error404 />
        },
        {
            path: `${path}:locale/job-not-found`,
            element: <ErrorJobNotFound />
        },
        {
            path: `${path}:locale/error-404`,
            element: <Error404 />
        },
        {
            path: `${path}/page-not-found`,
            element: <Error404 />
        },
        {
            path: `${path}/error-404`,
            element: <Error404 />
        },
        {
            path: `${path}:locale/unexpectedError`,
            element: <Error500 />
        },
        {
            path: `${path}:locale/error-500`,
            element: <Error500 />
        },
        {
            path: `${path}/error-500`,
            element: <Error500 />
        },
        {
            path: `${path}unexpectedError`,
            element: <Error500 />
        },
        {
            path: `${path}:locale/:cmsPageSlug`,
            element: <CmsPage />
        },
        {
            path: `${path}:locale/about/:cmsPageSlug`,
            element: <CmsPage />
        },
        {
            path: `${path}:locale/*`,
            element: <Error404 />
        }
    ]);
};

/**
 * Returns routes in an array that react-router expects, dropping the extra fields used for the server render
 */
export const getRoutes = (): RouteObject[] => {
    const serverRoutes = getServerRoutes();
    const routes = serverRoutes.map((route) => {
        return {
            path: route.path,
            element: route.element
        };
    });

    return routes;
};

export const useRoutesElement = (): React.ReactElement | null => {
    return useRoutes(getRoutes());
};

export const isClassicRoute = (pathname: string): boolean => {
    if (!pathname) return false;

    const noBootstrapPages = [
        '/:locale/careers/jobs',
        '/:locale/earlycareers',
        '/:locale/earlycareers/career-paths',
        '/:locale/tos'
    ];
    for (let i = 0; i < noBootstrapPages.length; i++) {
        const page = noBootstrapPages[i];
        for (const prefix of ["", "/site", "/site-staging"]) {
            const match = matchPath(`${prefix}${page}`, pathname);
            if (match) return false;
        }
    }

    return true;
};

export const isReactRoute = (pathname: string): PathMatch | null => {
    if (!pathname) return null;

    const routes = getRoutes();

    let match;
    for (let i = 0; i < routes.length; i++) {
        const route = routes[i];
        if (!match && route.path && pathname) {
            match = matchPath(route.path, pathname);
            if (match) break;
        }
    }

    return match;
};

export const isCmsPageRoute = (pathname: string): boolean => {
    const match = isReactRoute(pathname);
    const params = match?.params;
    const isCmsPage = params?.cmsPageSlug ? true : false;
    return isCmsPage;
};

export default getRoutes;
